import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { replaceAndStrip } from '../../../utils/string'
import { compile } from '../../../utils/search'

interface Label {
  id: number
  name: string
  color: string
  description: string | null
}

interface Props {
  currentLabelName: string | null
  callbackLabel: (label: string) => void
  labelList: Label[]
  existLabel: boolean
}

const SetLabelModule: FC<Props> = (props) => {
  const {
    currentLabelName,
    callbackLabel,
    labelList,
    existLabel,
  } = props

  const elementRef = useRef<HTMLDivElement>(null)

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [searchWord, setSearchWord] = useState<string>("")
  const [labels, setLabels] = useState<Label[]>([])

  const [labelText, setLabelText] = useState<string>("")

  const [displayLabel, setDisplayLabel] = useState<boolean>(false)
  const [selectLabelPosition, setSelectLabelPosition] = useState<{x: number, y: number}>({ x: 0, y: 0 })

  const [selectLabel, setSelectLabel] = useState<Label | null>(null)

  const labelListInit = useMemo(() => (
    labelList || []
  ), [labelList])

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setLabels([...labelListInit])
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = labelListInit.filter((label) => {
      if (label === null) return false
      return (compile(word).test(label.name.toLowerCase()))
    })
    setLabels(sortBy(list, 'id'))
  }

  const reset = () => {
    setSearchWord("")
    setLabelText("")
    setLabels([...labelListInit])
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const closeSearch = () => {
    setIsSearchDisplay(false)
    reset()
  }

  const addLabel = (labelName: string) => {
    callbackLabel(labelName)
    closeSearch()
  }

  const changeLabelText = (labelName: string) => {
    callbackLabel(labelName)
    setLabelText(labelName)
  }

  const hideSelectLabel = () => {
    setDisplayLabel(false)
    setSelectLabel(null)
  }

  const displaySelectLabel = (label: Label | null, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!label?.description) {
      hideSelectLabel()
      return
    }
    setDisplayLabel(true)
    setSelectLabel(label)
    setSelectLabelPosition({
      x: 0,
      y: event.clientY - (elementRef.current?.getBoundingClientRect().y || 0) - 36,
    })
  }

  useEffect(() => {
    setLabels(labelList || [])
  }, [labelList])

  useEffect(() => {
    if (existLabel) return
    setLabelText(currentLabelName || "")
  }, [currentLabelName])

  return (
    <>
      <div
        className="label_select_area"
        ref={elementRef}
      >
        <div
          className={`add_button ${currentLabelName ? "" : "pointer"}`}
          onClick={() => !currentLabelName && !isSearchDisplay && openSearch()}
          role="none"
        >
          {
            currentLabelName ? (
              <>
                {
                  existLabel ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="chip">
                        {currentLabelName}
                      </div>
                      <button type="button" className="reset" style={{ padding: "0" }} onClick={() => addLabel("")}>
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    </div>
                  ) : (
                    <div className="editable_area">
                      <input
                        type="text"
                        value={labelText}
                        onChange={(event) => changeLabelText(event.target.value)}
                        onBlur={() => changeLabelText(replaceAndStrip(labelText))}
                      />
                    </div>
                  )
                }
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    wordBreak: "keep-all",
                  }}
                >
                  <FontAwesomeIcon icon={['fas', 'plus']} />
                  <div>
                    追加
                  </div>
                </div>
                <div className={`search_box ${isSearchDisplay || 'none'}`}>
                  <div className="search_detail">
                    <div className="category">
                      <div className="category_title">
                        業務一覧
                      </div>
                      <div className="create_label_area">
                        <div className="search_area">
                          <FontAwesomeIcon
                            icon={['fas', 'search']}
                            className="icon"
                          />
                          <input
                            type="text"
                            placeholder="業務名を検索or新しい業務名を入力"
                            onChange={(event) => search(event.target.value)}
                            value={searchWord}
                          />
                          {
                            searchWord && (
                              <button type="button" className="reset" onClick={reset}>
                                <FontAwesomeIcon icon={['far', 'times-circle']} />
                              </button>
                            )
                          }
                        </div>
                        <button
                          type="button"
                          className="add_label"
                          onClick={() => addLabel(replaceAndStrip(searchWord))}
                          disabled={!searchWord.length}
                        >
                          新しい業務を作成
                        </button>
                      </div>
                      <div className="chip_list">
                        {
                          labels.map((label) => (
                            <button
                              type="button"
                              className="chip"
                              key={`label_${label.name}`}
                              onClick={() => addLabel(label.name)}
                              onMouseMove={(e) => displaySelectLabel(label, e)}
                              onMouseLeave={() => hideSelectLabel()}
                              onFocus={() => { /* none */ }}
                            >
                              {label.name}
                            </button>
                          ))
                        }
                        {
                          !labels.length && (
                            <>
                              <div className="no_result">
                                業務がありません
                              </div>
                            </>
                          )
                        }
                      </div>
                      {
                        displayLabel && (
                          <div
                            style={{
                              position: "absolute",
                              top: `${selectLabelPosition.y}px`,
                              left: `${selectLabelPosition.x}px`,
                              width: "100%",
                              backgroundColor: "#edfbfb",
                              padding: "8px 16px",
                              border: "solid 1px #4a91dd",
                              borderRadius: "4px",
                              zIndex: "2000",
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                              }}
                            >
                              <FontAwesomeIcon icon={['far', 'comment-dots']} />
                            </div>
                            <div>
                              {
                                selectLabel && selectLabel.description?.split('\n').map((v) => (
                                  <div
                                    style={{
                                      minHeight: "1em",
                                    }}
                                  >
                                    {v}
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </>
            )
          }
        </div>
      </div>
      <div className={`close_screen gray ${isSearchDisplay || 'none'}`} role="presentation" onClick={() => closeSearch()} />
    </>
  )
}

export default SetLabelModule
